/**
 * useChargeRun - a Charge runs, then strikes (2026-10-21: "charge for some reason demands being in melee range of
 * the objective, which completely misses the point").
 *
 * Wraps the strike every path fires (the drum's Charge plate, the Charge pill and the trigger). With the `charge`
 * toggle on, a melee weapon and the map able to decide (`useChargeLane`):
 * - refused (too close, too far, blocked, a climb, prone, unable to move): the reason in the log, no strike;
 * - otherwise the run: the move's line first (every screen walks it off the line, `handleMapMove`), then the token,
 *   through the hidden-aware writer; and once THIS screen's rules see the token at the stop - so reach, cover and
 *   the arc are read from where the run ended - the kill zones are asked (`onTokenSettled`) and the strike lands.
 *   The charger's starting token and the run's own move line (its column, and its timestamp - the run's start)
 *   are handed to the undo (`chargeOriginRef`) as the strike goes: an undone charge puts the charger back and
 *   deletes exactly that line. A hidden charger's run writes no public line, so it hands none. A run that never
 *   lands is called off after 4 s.
 * With no map to decide (positions off, the map's modifiers off) it strikes where it stands, as it always did.
 *
 * @param {object} o
 * @param {{ plan: object | null, band: number, refusal: string | null }} o.lane - useChargeLane
 * @param {boolean} o.charging - the `charge` toggle with a melee weapon
 * @param {object | null} o.actor
 * @param {string} o.activeTab
 * @param {Record<string, object>} o.tokens - the rule tokens
 * @param {Function} o.writeToken - the hidden-aware token writer
 * @param {Function} o.handleMapMove - the move's public line
 * @param {Function} [o.onTokenSettled] - the kill zones' question
 * @param {Function} o.refuse - (text) => void: a refusal's line in the log
 * @param {{ current: { token: object, line: { column: string, actorId: string, at: number } | null } | null }}
 *   [o.chargeOriginRef] -
 *   where the undo finds the starting token and the run's move line
 * @param {(id: string) => boolean} [o.isHidden] - a figure hidden from the players
 * @returns {(strike: () => void) => void}
 */
import { useCallback, useEffect, useRef } from 'react';
import { chargeLine } from '../engine/battlefield/charge';

/** Close enough to the stop to call the run done (the writer may round). */
const AT_STOP_M = 0.01;

const atStop = (token, stop) => !!token && Math.hypot(token.x - stop.x, token.z - stop.z) <= AT_STOP_M;

/** The run, written: the line (never for a hidden figure: its route is the GM's), then the token at the stop. */
function run(o, plan, strike) {
  const from = o.tokens[o.actor.id];
  // The move line's timestamp: the undo deletes exactly that entry (useChargeUndo `dropLine`).
  const startedAt = Date.now();
  const { stop } = plan;
  const facts = {
    at: startedAt,
    actorId: o.actor.id,
    name: o.actor.name,
    from,
    to: stop,
    path: [],
    mode: 'charge',
    metres: plan.metres,
  };
  const hidden = !!o.isHidden?.(o.actor.id);
  // What the move does to him (a lost aim, leaving melee: 2026-10-26) - a hidden charger's too.
  o.onMoveSettled?.(facts);
  if (!hidden) o.handleMapMove(facts);
  o.writeToken({ ...from, ...stop, rev: (from.rev ?? 0) + 1 }, { immediate: true });
  // The line went to the target's column (the shell's active tab); a hidden charger wrote none. Never a line without
  // its column: the shared history is a document, and refuses an undefined field (gotcha 2).
  const line = hidden || !o.activeTab ? null : { column: o.activeTab, actorId: o.actor.id, at: startedAt };
  return { actorId: o.actor.id, targetId: o.activeTab, stop, strike, from, line };
}

/** A press of the trigger with the charge on: strike now, refuse, or start the run (its pending strike). */
function begin(o, strike) {
  const { plan, refusal, band } = o.lane;
  if (!o.charging || (!plan && !refusal)) {
    strike();
    return null;
  }
  if (!plan || !plan.ok) {
    o.refuse(`🔴 ${chargeLine(plan ?? { ok: false, reason: refusal }, band)}`);
    return null;
  }
  return run(o, plan, strike);
}

/** Has the run landed on this screen's rules? Then the kill zones are asked and the strike goes. */
function land(pending, o) {
  const p = pending.current;
  if (!p) return;
  // The actor or the target changed while the token was on its way: the charge is off.
  if (o.actor?.id !== p.actorId || o.activeTab !== p.targetId) {
    pending.current = null;
    return;
  }
  if (!atStop(o.tokens?.[p.actorId], p.stop)) return;
  pending.current = null;
  o.onTokenSettled?.(p.actorId);
  // The start of the run, for the undo the swing's snapshot makes - handed over now, and taken back if the swing
  // was refused before it snapshotted, so no later action carries it.
  if (o.chargeOriginRef) o.chargeOriginRef.current = { token: p.from, line: p.line };
  p.strike();
  if (o.chargeOriginRef) o.chargeOriginRef.current = null;
}

/** How long a run may take to reach this screen's rules before the charge is called off. */
const LAND_WITHIN_MS = 4000;

export default function useChargeRun(o) {
  const pending = useRef(null);
  const live = useRef(o);
  live.current = o;
  const charge = useCallback((strike) => {
    const p = begin(live.current, strike);
    pending.current = p;
    if (!p) return;
    // A write refused or overtaken never lands: the charge is off, and says so, instead of waiting for ever.
    setTimeout(() => {
      if (pending.current !== p) return;
      pending.current = null;
      live.current.refuse('🔴 The charge never reached its stop.');
    }, LAND_WITHIN_MS);
  }, []);
  const { tokens, actor, activeTab, onTokenSettled, chargeOriginRef } = o;
  useEffect(
    () => land(pending, { tokens, actor, activeTab, onTokenSettled, chargeOriginRef }),
    [tokens, actor, activeTab, onTokenSettled, chargeOriginRef],
  );
  return charge;
}
